import { getProviderTypes, NotificationProviderType, UserNotification } from "@doitintl/cmp-models";
import EmailIcon from "@mui/icons-material/Email";
import { Box, Tooltip } from "@mui/material";

import SlackIcon from "../../../assets/slack-mark.png";
import MSTeamsIcon from "../../../assets/teams.svg";
import { never } from "../../../utils";
import { toSentenceCase } from "../../../utils/string";
import texts from "./texts";
import { type NotificationModelWithId, type TableNotification } from "./types";

export const providerIconCyIds: Record<NotificationProviderType, string> = {
  [NotificationProviderType.EMAIL]: "provider-icon-email",
  [NotificationProviderType.SLACK]: "provider-icon-slack",
  [NotificationProviderType.MSTEAMS]: "provider-icon-msteams",
};

export const getProviderIcon = (providerType: NotificationProviderType) => {
  switch (providerType) {
    case NotificationProviderType.EMAIL:
      return (
        <Tooltip title="Email">
          <Box data-cy={providerIconCyIds[providerType]}>
            <EmailIcon fontSize="small" sx={{ color: "action.active" }} />
          </Box>
        </Tooltip>
      );

    case NotificationProviderType.SLACK:
      return (
        <Tooltip title="Slack">
          <Box data-cy={providerIconCyIds[providerType]}>
            <img alt="slack" src={SlackIcon} width="20" height="20" />
          </Box>
        </Tooltip>
      );

    case NotificationProviderType.MSTEAMS:
      return (
        <Tooltip title="MSTeams">
          <Box
            component="img"
            alt="msteams"
            src={MSTeamsIcon}
            data-cy={providerIconCyIds[providerType]}
            sx={{
              width: "20",
              height: "20",
            }}
          />
        </Tooltip>
      );

    default:
      return never(providerType, "");
  }
};

const channelsToString = (notification: NotificationModelWithId, providerType: NotificationProviderType) => {
  const { providerTarget } = notification;
  const channels = providerTarget?.[providerType];
  if (providerType === NotificationProviderType.EMAIL) {
    return channels?.join(", ");
  } else if (providerType === NotificationProviderType.MSTEAMS) {
    return channels?.map((channel) => `${channel.channel?.team_name} > ${channel.channel?.name}`).join(", ");
  } else {
    return channels?.map((channel) => channel.name).join(", ");
  }
};

export const parseNotifications = (
  notifications: NotificationModelWithId[] = [],
  notificationsData?: { name: string; value: number }[]
): TableNotification[] => {
  const singleProviderNotifications = notifications.filter(
    (notification) => getProviderTypes(notification).length === 1
  );
  return singleProviderNotifications?.map((notification) => {
    const selectedNotifications = Object.keys(notification.selectedNotifications)
      .map((key) => notificationsData?.find((n) => n.value.toString() === key)?.name)
      .filter((name): name is string => name !== undefined);

    const providerType = getProviderTypes(notification)[0];
    return {
      ...notification,
      selectedNotifications: selectedNotifications.join(","),
      providerTarget: channelsToString(notification, providerType),
      providerType,
    };
  });
};

export const editCustomerNotification = (customerId: string, notificationId: string): string =>
  `/customers/${customerId}/company/notifications/edit/${notificationId}`;

export const editUserNotification = (customerId: string, notificationId: string): string => {
  if (notificationId === "in-app") {
    return `/customers/${customerId}/notifications/in-app/edit`;
  }
  return `/customers/${customerId}/user/notifications/edit/${notificationId}/2`;
};

/**
 * Converts an array of notification keys into a comma-separated string of UserNotification names
 * @param notifications Array of numbers corresponding to UserNotification enum values
 * @returns A comma-separated string of UserNotification names, omitting any unknown notification key values
 */
export function userNotificationsToString(notifications: number[]): string {
  if (notifications.length === 0) {
    return texts.table.row.noneSet;
  }

  return notifications
    .map((n) => UserNotification[n])
    .filter((name): name is string => name !== undefined)
    .map((name) => toSentenceCase(name))
    .join(", ");
}

export function providerTypeToString(providerType: NotificationProviderType): string {
  const providerTypeStrings = {
    [NotificationProviderType.EMAIL]: "Email",
    [NotificationProviderType.SLACK]: "Slack",
    [NotificationProviderType.MSTEAMS]: "MS Teams",
  };

  if (!providerTypeStrings[providerType]) {
    return "Unknown";
  }

  return providerTypeStrings[providerType];
}
